import { Box, ClickAwayListener, Icon, Popper, Typography } from '@mui/material';
import { AdvanceFilter, AppRoutes, getSortComparer, RouteName, useAppSelector } from 'common';
import { selectAllProductShoppingCategories } from 'common/src/store/product-shopping-categories/product-shopping-category.slice';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CATEGORY_ROW_HEIGHT, StyledAppBar } from '../Header.styles';

const CATEGORY_SECTION_SPACING = 2;

const CategoryNavigation: FC = () => {
  const categories = useAppSelector(selectAllProductShoppingCategories);
  const history = useHistory();
  const rootCategories = useMemo(
    () => categories.filter((c) => c.Level === 0).sort(getSortComparer((x) => x.Sequence)),
    [categories]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeRootId, setActiveRootId] = useState<string | null>(null);

  const allItemsRef = useRef<HTMLElement>();
  const selectedCategoryRef = useRef<HTMLElement>();

  const handleOpen = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    rootCategoryId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveRootId(rootCategoryId);
  };

  const handleClose = () => {
    allItemsRef.current = undefined;
    selectedCategoryRef.current = undefined;
    setAnchorEl(null);
    setActiveRootId(null);
  };

  const activeRootCategory = useMemo(
    () => categories.find((c) => c.ProductShoppingCategoryId === activeRootId),
    [categories, activeRootId]
  );

  const sectionCategories = useMemo(
    () =>
      categories
        .filter((c) => c.ParentProductShoppingCategoryId === activeRootId)
        .sort((a, b) => a.Sequence - b.Sequence),
    [categories, activeRootId]
  );

  const getChildren = (parentId: string) =>
    categories.filter((c) => c.ParentProductShoppingCategoryId === parentId).sort(getSortComparer((x) => x.Sequence));

  const filters = useMemo(() => {
    if (!activeRootCategory?.Filters) return [];
    return activeRootCategory.Filters.split('|')
      .map((f) => f.trim())
      .filter((f) => !!f);
  }, [activeRootCategory]);

  const columnCount = useMemo(() => {
    const count = sectionCategories.length;
    if (count >= 12) return 5;
    if (count >= 8) return 4;
    if (count >= 4) return 3;
    return 2;
  }, [sectionCategories.length]);

  const handleCategory = (
    categoryId: string,
    subcategoryId?: string,
    filter?: string,
    isExclusiveProduct?: boolean
  ) => {
    handleClose();
    const query = subcategoryId ? `?subcategoryId=${subcategoryId}` : '';
    const advanceFilter: AdvanceFilter | undefined =
      filter || isExclusiveProduct
        ? {
            Badges: [],
            CategoryIds: [],
            Brands: [],
            StorageTypes: [],
            StateOfOriginAbbreviations: [],
            DeliveryOptions: {},
            Nutritional: {},
            ShoppingCategoryFilters: filter ? [filter] : undefined,
            IsExclusiveProduct: isExclusiveProduct,
            Manufacturers: [],
          }
        : undefined;
    history.push(`${AppRoutes[RouteName.CategorySearch].Path}/${categoryId}${query}`, { advanceFilter });
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        // onMouseLeave={(e) => {
        //   const { top } = e.currentTarget.getBoundingClientRect();
        //   const mouseY = e.clientY;
        //   if (mouseY < top) {
        //     return handleClose();
        //   }
        // }}
        sx={{ width: '100vw', borderTop: '1px solid #E5E5E5' }}
      >
        <StyledAppBar
          sx={{
            minHeight: CATEGORY_ROW_HEIGHT - 1,
            overflow: 'visible',
            alignItems: 'center',
            color: 'text.primary',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, width: '100%' }}>
            <Typography variant='paragraph' color={(theme) => theme.palette.grey[600]}>
              Shop:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexGrow: 1 }}>
              {rootCategories.map((category) => (
                <Box
                  component='div'
                  key={category.ProductShoppingCategoryId}
                  onClick={(e) => {
                    selectedCategoryRef.current = e.currentTarget;
                    handleOpen(e, category.ProductShoppingCategoryId);
                  }}
                  onKeyDown={(e) => {
                    if (e.code.toLowerCase() === 'enter') {
                      selectedCategoryRef.current = e.currentTarget;
                      handleOpen(e, category.ProductShoppingCategoryId);
                    }
                    if (Boolean(anchorEl)) {
                      if (e.code.toLowerCase() === 'tab' && !e.shiftKey) {
                        console.log(allItemsRef.current);
                        //anchorEl?.focus();
                        allItemsRef.current?.focus();
                        e.preventDefault();
                      }
                    }
                  }}
                  //onMouseEnter={(e) => handleOpen(e, category.ProductShoppingCategoryId)}
                  sx={{
                    cursor: 'pointer',
                    typography: 'paragraphBold',
                    color: (theme) => theme.palette.grey[600],
                    ...(activeRootId === category.ProductShoppingCategoryId
                      ? {
                          textDecoration: 'underline',
                          textDecorationThickness: '3px',
                          textUnderlineOffset: '8px',
                        }
                      : {}),
                    '&:hover': {
                      textDecoration: 'underline',
                      textDecorationThickness: '3px',
                      textUnderlineOffset: '8px',
                    },
                  }}
                  tabIndex={0}
                >
                  {category.Title}
                </Box>
              ))}
              <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement='bottom-start'
                modifiers={[
                  {
                    name: 'offset',
                    options: { offset: [0, 8] },
                  },
                ]}
                sx={{
                  width: '100vw',
                  left: '0 !important',
                  zIndex: 1300,
                }}
              >
                <Box
                  onMouseLeave={(e) => {
                    const { bottom } = e.currentTarget.getBoundingClientRect();
                    const mouseY = e.clientY;
                    if (mouseY > bottom) {
                      return handleClose();
                    }
                  }}
                  sx={{
                    backgroundColor: 'white',
                    py: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <StyledAppBar
                    sx={{
                      alignItems: 'flex-start',
                      minHeight: 'auto',
                      color: 'text.primary',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr minmax(200px, 220px)',
                        columnGap: 8,
                      }}
                    >
                      <Box
                        sx={{
                          columnCount,
                          columnGap: 8,
                        }}
                      >
                        <Box
                          ref={(e: HTMLElement) => (allItemsRef.current = e)}
                          onClick={() => handleCategory(activeRootCategory?.ProductShoppingCategoryId ?? '')}
                          onKeyDown={(e) => {
                            if (e.code.toLowerCase() === 'enter') {
                              handleCategory(activeRootCategory?.ProductShoppingCategoryId ?? '');
                            }
                            if (Boolean(anchorEl)) {
                              if (e.code.toLowerCase() === 'tab' && e.shiftKey) {
                                e.preventDefault();
                                selectedCategoryRef.current?.focus();
                                handleClose();
                              }
                            }
                          }}
                          sx={{
                            breakInside: 'avoid',
                            display: 'flex',
                            gap: 0.25,
                            mb: CATEGORY_SECTION_SPACING,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          tabIndex={0}
                        >
                          <Typography
                            color={(theme) => theme.palette.grey[600]}
                            sx={{
                              typography: 'paragraphBold',
                            }}
                          >
                            All {activeRootCategory?.Title}
                          </Typography>
                          <Icon
                            className='fa-chevron-right'
                            fontSize='small'
                            sx={{ color: (theme) => theme.palette.grey[600], textDecoration: 'unset !important' }}
                          />
                        </Box>

                        {sectionCategories.map((section) => (
                          <Box
                            key={section.ProductShoppingCategoryId}
                            sx={{
                              breakInside: 'avoid',
                              mb: CATEGORY_SECTION_SPACING,
                              gap: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              onClick={() =>
                                handleCategory(
                                  activeRootCategory?.ProductShoppingCategoryId ?? '',
                                  section.ProductShoppingCategoryId
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.code.toLowerCase() === 'enter') {
                                  handleCategory(
                                    activeRootCategory?.ProductShoppingCategoryId ?? '',
                                    section.ProductShoppingCategoryId
                                  );
                                }
                              }}
                              tabIndex={0}
                              sx={{
                                breakInside: 'avoid',
                                display: 'flex',
                                gap: 0.25,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <Typography variant='paragraphBold' color={(theme) => theme.palette.grey[600]}>
                                {section.Title}
                              </Typography>{' '}
                              <Icon
                                className='fa-chevron-right'
                                fontSize='small'
                                sx={{
                                  color: (theme) => theme.palette.grey[600],
                                }}
                              />
                            </Box>

                            {getChildren(section.ProductShoppingCategoryId).map((item) => (
                              <Box
                                color={(theme) => theme.palette.grey[600]}
                                key={item.ProductShoppingCategoryId}
                                tabIndex={0}
                                sx={{
                                  cursor: 'pointer',
                                  typography: 'paragraph',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={() =>
                                  handleCategory(
                                    activeRootCategory?.ProductShoppingCategoryId ?? '',
                                    item.ProductShoppingCategoryId
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.code.toLowerCase() === 'enter') {
                                    handleCategory(
                                      activeRootCategory?.ProductShoppingCategoryId ?? '',
                                      item.ProductShoppingCategoryId
                                    );
                                  }
                                }}
                              >
                                {item.Title}
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: CATEGORY_SECTION_SPACING }}>
                        {filters.map((filter) => (
                          <Box
                            key={filter}
                            sx={{
                              breakInside: 'avoid',
                              display: 'flex',
                              gap: 0.25,
                              mb: CATEGORY_SECTION_SPACING,
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            tabIndex={0}
                            onClick={() =>
                              handleCategory(activeRootCategory?.ProductShoppingCategoryId ?? '', undefined, filter)
                            }
                            onKeyDown={(e) => {
                              if (e.code.toLowerCase() === 'enter') {
                                handleCategory(activeRootCategory?.ProductShoppingCategoryId ?? '', undefined, filter);
                              }
                            }}
                          >
                            <Typography variant='paragraphBold' color={(theme) => theme.palette.grey[600]}>
                              {filter}
                            </Typography>
                            <Icon
                              className='fa-chevron-right'
                              fontSize='small'
                              sx={{
                                color: (theme) => theme.palette.grey[600],
                                textDecoration: 'none !important',
                              }}
                            />
                          </Box>
                        ))}
                        <Box
                          sx={{
                            breakInside: 'avoid',
                            display: 'flex',
                            gap: 0.25,
                            mb: CATEGORY_SECTION_SPACING,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          tabIndex={0}
                          onClick={() =>
                            handleCategory(
                              activeRootCategory?.ProductShoppingCategoryId ?? '',
                              undefined,
                              undefined,
                              true
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.code.toLowerCase() === 'enter') {
                              handleCategory(
                                activeRootCategory?.ProductShoppingCategoryId ?? '',
                                undefined,
                                undefined,
                                true
                              );
                            }
                          }}
                        >
                          <Typography variant='paragraphBold' color={(theme) => theme.palette.grey[600]}>
                            Our Brands
                          </Typography>
                          <Icon
                            className='fa-chevron-right'
                            fontSize='small'
                            sx={{
                              color: (theme) => theme.palette.grey[600],
                              textDecoration: 'none !important',
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </StyledAppBar>
                </Box>
              </Popper>
            </Box>
          </Box>
        </StyledAppBar>
      </Box>
    </ClickAwayListener>
  );
};
export default CategoryNavigation;
